import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { Spinner, Button, Form, InputGroup } from "react-bootstrap";
import { connect } from "react-redux";
import Icon from "@mdi/react";
import { mdiArrowDownCircle, mdiPencilOutline, mdiMagnify, mdiTrashCanOutline, mdiShareVariantOutline, mdiFolderOutline } from "@mdi/js";
import Drawer from "@mui/material/Drawer";
import "./ProForms.scss";
import { Hidden } from "@mui/material";

const DocumentOrdersTable = ({ entity, userId }) => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterByUser, setFilterByUser] = useState(true);
  const pageCount = 8; // Items per page
  const [currentIndex, setCurrentIndex] = useState(0);

  // Drawer states
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // New state to track drawer mode: "edit" vs "annexes"
  const [drawerMode, setDrawerMode] = useState("edit");
  // State to hold annexes data
  const [annexes, setAnnexes] = useState([]);

  useEffect(() => {
    if (entity && entity.id) {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [searchTerm]);

  const parseField = (field) => {
    if (typeof field === "string") {
      try {
        return JSON.parse(field);
      } catch (err) {
        console.error("Error parsing field:", field, err);
        return {};
      }
    }
    return field || {};
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/Api/document-getorders/entity/${entity.id}`
      );
      if (response.data) {
        console.log(response.data);
        const orders = response.data.map((order) => {
          const buyerObj = parseField(order.buyer);
          const sellerObj = parseField(order.seller);
          return {
            ...order,
            buyer_name: buyerObj.name || "",
            buyer_email: buyerObj.email || "",
            buyer_address: buyerObj.address || "",
            seller_name: sellerObj.name || "",
            seller_email: sellerObj.email || "",
            seller_address: sellerObj.address || "",
          };
        });
        setOrders(orders);
      }
    } catch (error) {
      console.error("Error fetching document orders:", error);
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "warning";
      case "in progress":
        return "info";
      case "completed":
        return "primary";
      case "cancelled":
        return "danger";
      case "declined":
        return "secondary";
      case "disputed":
        return "danger";
      case "refunded":
        return "dark";
      default:
        return "primary";
    }
  };

  const getStatusLabel = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "En attente";
      case "in progress":
        return "En instruction";
      case "completed":
        return "Disponible";
      case "cancelled":
        return "Annulé";
      case "declined":
        return "Echec paiement";
      case "disputed":
        return "Litige";
      case "refunded":
        return "Remboursé";
      default:
        return "Tous";
    }
  };

  const handleOpenPDFForm = async (order) => {
    try {
      const response = await axios.get(order.url, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${order.document_order_ref}-${order.reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error downloading the PDF:", error);
    }
  };

  // Existing edit click function
  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setDrawerMode("edit");
    setShowEditDrawer(true);
  }; ``

  // New function for annexes
  const handleAnnexesClick = async (order) => {
    setSelectedOrder(order);
    setDrawerMode("annexes");
    try {
      const response = await axios.post(
        "/Api/geodata/parcelle/getAnnexesByParcelleList",
        { key_parcelles: order.parcelle } // pass the parcelle as an array param
      );
      console.log(response.data);
      setAnnexes(response.data);
    } catch (error) {
      console.error("Error fetching annexes:", error);
      setAnnexes([]);
    }
    setShowEditDrawer(true);
  };

  // Close drawer and reset annexes and mode
  const handleCloseDrawer = () => {
    setShowEditDrawer(false);
    setSelectedOrder(null);
    setDrawerMode("edit");
    setAnnexes([]);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const buyer = {
      name: selectedOrder.buyer_name,
      email: selectedOrder.buyer_email,
      address: selectedOrder.buyer_address,
    };
    const seller = {
      name: selectedOrder.seller_name,
      email: selectedOrder.seller_email,
      address: selectedOrder.seller_address,
    };

    const payload = {
      buyer: buyer,
      seller: seller,
      requestor_ref: selectedOrder.requestor_ref,
    };

    try {
      await axios.put(
        `/Api/document-orders/entity-update/${selectedOrder.document_order_id}`,
        payload
      );
      console.log("Saving updated order:", payload);
      setShowEditDrawer(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filterByUser && order.user_id !== userId) return false;
    const searchLower = searchTerm.toLowerCase();

    return (
      (order.buyer_name || "").toLowerCase().includes(searchLower) ||
      (order.seller_name || "").toLowerCase().includes(searchLower) ||
      (order.document_orders_commune || "").toLowerCase().includes(searchLower) ||
      (order.parcelle || "").toLowerCase().includes(searchLower) ||
      (order.document_orders_reference || "").toLowerCase().includes(searchLower) ||
      (order.reference || "").toLowerCase().includes(searchLower) ||
      (order.requestor_ref || "").toLowerCase().includes(searchLower)
    );
  });


  const renderRow = (order) => {
    const formattedParcelle = order.parcelle.split(",").map(p => p.slice(-6)).join(", ");

    return (
      <tr key={order.id}>
        <td className="text-capitalize" style={{ width: "120px", textAlign: "center" }}>
          {order.is_new ? (
            <span
              className="status-indicator-badge badge"
              style={{ backgroundColor: "var(--theme-urgent)", color: "#FFFFFF" }}
            >
              NON LU
            </span>
          ) : (
            <span className={`status-indicator-badge badge badge-${getStatusBadge(order.status)}`}>
              {getStatusLabel(order.status)}
            </span>
          )}
        </td>
        <td style={{ width: "125px" }} className="doc-orders-table-td">
          {order.document_orders_reference}
        </td>
        <td style={{ textAlign: "center" }}>{order.reference}</td>
        <td style={{ maxWidth: "100px" }} className="doc-orders-table-td text-left">
          {order.document_orders_commune}
        </td>
        <td
          style={{ maxWidth: "100px" }}
          className="doc-orders-table-td text-left"
          title={formattedParcelle}
        >
          {formattedParcelle}
        </td>
        <td style={{ maxWidth: "135px" }} className="d-none d-lg-table-cell doc-orders-table-td text-left">
          {order.user_full_name}
        </td>
        <td className="d-none d-xl-table-cell">
          {order.buyer_name ? (
            order.buyer_name
          ) : (
            <span style={{ color: '#888', fontStyle: 'italic' }}>Non renseigné</span>
          )}
        </td>
        <td className="d-none d-xl-table-cell">
          {order.seller_name ? (
            order.seller_name
          ) : (
            <span style={{ color: '#888', fontStyle: 'italic' }}>Non renseigné</span>
          )}
        </td>
        <td style={{ maxWidth: "100px" }} className="doc-orders-table-td">
          {order.requestor_ref ? (
            order.requestor_ref
          ) : (
            <span style={{ color: '#888', fontStyle: 'italic' }}>Non renseigné</span>
          )}
        </td>
        <td className="d-none d-lg-table-cell text-center">
          {moment(order.created_at).format("L")}
        </td>
        <td className="d-none d-lg-table-cell text-center">
          {order.status === "completed" ? moment(order.updated_at).format("L") : "-"}
        </td>
        <td
          style={{ maxWidth: "64px" }}
          className="text-center ue-dashboard-btn"
          onClick={() => handleEditClick(order)}
        >
          <Icon path={mdiPencilOutline} size={0.9} color={"#333333"} />
        </td>
        <td
          style={{ maxWidth: "64px" }}
          className="text-center ue-dashboard-btn"
          onClick={() => handleEditClick(order)}
        >
          <Icon path={mdiShareVariantOutline} size={0.9} color={"#333333"} />
        </td>
        <td
          style={{ maxWidth: "64px" }}
          className="text-center ue-dashboard-btn"
          onClick={() => handleAnnexesClick(order)}
        >
          <Icon path={mdiFolderOutline} size={0.9} color={"#333333"} />
        </td>
        <td style={{ maxWidth: "64px" }} className="text-center ue-dashboard-btn">
          {order?.annex_url ? (
            <a href={order.annex_url} target="_blank" rel="noopener noreferrer">
              <Icon path={mdiArrowDownCircle} size={0.9} color={"var(--theme-primary)"} />
            </a>
          ) : (
            <Icon path={mdiArrowDownCircle} size={0.9} color={"#d2d2d2"} />
          )}
        </td>
        <td style={{ maxWidth: "64px" }} className="text-center ue-dashboard-btn">
          {order?.url ? (
            <a href={order.url} target="_blank" rel="noopener noreferrer">
              <Icon path={mdiArrowDownCircle} size={0.9} color={"var(--theme-primary)"} />
            </a>
          ) : (
            <Icon path={mdiArrowDownCircle} size={0.9} color={"#d2d2d2"} />
          )}
        </td>
      </tr>
    );
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 0; i < filteredOrders.length; i += pageCount) {
      const pageNumber = Math.floor(i / pageCount) + 1;
      pages.push(
        <li key={i} className="page-item">
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setCurrentIndex(i);
            }}
          >
            {pageNumber}
          </a>
        </li>
      );
    }
    return pages;
  };

  return (
    <div className="card rounded-0 mt-4 text-black w-100 h-100 border-0">
      <div className="card-body text-dark">
        <div className="d-flex justify-content-between">
          <h2 className="card-title mb-4">Formulaires</h2>
          {/* Radio buttons for filtering */}
          <div className="ms-3 d-flex flex-row-reverse">
            <div className="mr-1 proform-custom-control proform-custom-radio me-3">
              <input
                type="radio"
                className="proform-custom-control-input"
                id="filterAll"
                name="orderFilter"
                checked={!filterByUser}
                onChange={() => setFilterByUser(false)}
              />
              <label className="proform-custom-control-label" htmlFor="filterAll">
                Tous les dossiers
              </label>
            </div>
            <div className="ml-1 mr-1 proform-custom-control proform-custom-radio">
              <input
                type="radio"
                className="proform-custom-control-input"
                id="filterMine"
                name="orderFilter"
                checked={filterByUser}
                onChange={() => setFilterByUser(true)}
              />
              <label className="proform-custom-control-label" htmlFor="filterMine">
                Mes dossiers
              </label>
            </div>
          </div>
        </div>


        {/* Search Form */}
        <div className="d-flex justify-content-center w-100">
          <Form className="mb-3 border ue-form-control-wrap justify-content-center" style={{ width: "80%" }}>
            <Form.Group className="mb-0" controlId="search">
              <InputGroup style={{ verticalAlign: "middle" }}>
                <InputGroup.Text
                  style={{
                    backgroundColor: "#f1f4f4",
                    border: "none",
                    padding: "0.375rem 0.75rem",
                    verticalAlign: "middle",
                  }}
                >
                  {searchTerm ? (
                    <Icon
                      path={mdiTrashCanOutline}
                      size={0.8}
                      color="gray"
                      style={{ cursor: "pointer" }}
                      onClick={() => setSearchTerm("")}
                    />
                  ) : (
                    <Icon path={mdiMagnify} size={0.8} color="gray" />
                  )}
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Rechercher par acquéreur, vendeur, commune, parcelle, référence ou numéro de dossier"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderLeft: "none",
                    verticalAlign: "middle",
                    height: "38px",

                    backgroundColor: "#f1f4f4",
                  }}
                  className="form-control border-0"
                />
              </InputGroup>
            </Form.Group>
          </Form>
        </div>

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p>Chargement...</p>
          </div>
        ) : (
          <div className="d-flex flex-column overflow-hidden">
            <table className="table ue-dashboard-table-v3">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>Etat</th>
                  <th style={{ textAlign: "center" }}>Dossier</th>
                  <th style={{ textAlign: "center" }}>Référence</th>
                  <th>Commune</th>
                  <th>Parcelles</th>
                  <th className="d-none d-lg-table-cell">Demandeur</th>
                  <th className="d-none d-xl-table-cell">Acquéreur</th>
                  <th className="d-none d-xl-table-cell">Vendeur</th>
                  <th>Votre Référence</th>
                  <th className={"d-none d-lg-table-cell text-center"}>Demande</th>
                  <th className={"d-none d-lg-table-cell text-center"}>Livraison</th>
                  <th style={{ textAlign: "center" }}>Modifier</th>
                  <th style={{ textAlign: "center" }}>Partage</th>
                  <th style={{ textAlign: "center" }}>Annexes</th>
                  <th style={{ textAlign: "center" }}>Annexes PDF</th>
                  <th style={{ textAlign: "center" }}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length ? (
                  filteredOrders.slice(currentIndex, currentIndex + pageCount).map(renderRow)
                ) : (
                  <tr>
                    <td colSpan="13" className="text-center">
                      Aucune demande ne correspond à votre recherche.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {filteredOrders.length > pageCount && (
              <nav aria-label="Page navigation">
                <ul className="pagination justify-content-center">
                  <li className="page-item">
                    <a
                      className="page-link"
                      href="#"
                      aria-label="First"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentIndex(0);
                      }}
                    >
                      &laquo;
                    </a>
                  </li>
                  {renderPagination()}
                  <li className="page-item">
                    <a
                      className="page-link"
                      href="#"
                      aria-label="Last"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentIndex(Math.max(filteredOrders.length - pageCount, 0));
                      }}
                    >
                      &raquo;
                    </a>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        )}
      </div>

      <Drawer
        anchor="right"
        open={showEditDrawer}
        onClose={handleCloseDrawer}
        style={{ zIndex: 10001 }}
        PaperProps={{ sx: { width: "600px", height: "100vh", padding: "1rem" } }}
      >
        {selectedOrder && drawerMode === "edit" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h1 style={{ textAlign: "center", width: "100%" }}>
                Modifier <br />
                {selectedOrder.requestor_ref || "la demande"}
              </h1>
              <div className="ue-drawer-close" onClick={handleCloseDrawer}>
                <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M14.2812 6.6L11.3433 9.449L8.40537 6.6L6.80597 8.151L9.74388 11L6.80597 13.849L8.40537 15.4L11.3433 12.551L14.2812 15.4L15.8806 13.849L12.9427 11L15.8806 8.151L14.2812 6.6ZM11.3433 0C5.07045 0 0 4.917 0 11C0 17.083 5.07045 22 11.3433 22C17.6161 22 22.6866 17.083 22.6866 11C22.6866 4.917 17.6161 0 11.3433 0ZM11.3433 19.8C6.3409 19.8 2.26866 15.851 2.26866 11C2.26866 6.149 6.3409 2.2 11.3433 2.2C16.3457 2.2 20.4179 6.149 20.4179 11C20.4179 15.851 16.3457 19.8 11.3433 19.8Z"
                    fill="#9A9A9A"
                  />
                </svg>
              </div>
            </div>
            <Form onSubmit={handleSave}>
              <div className="justify-content-between">
                <div>
                  {/* Edit form fields */}
                  <div className="row mt-3 align-items-start">
                    <div className="col">
                      <h5 className="mt-3 mb-3">Référence dossier</h5>
                      <div className="did-floating-label-content">
                        <input
                          className="did-floating-input"
                          type="text"
                          id="requestor_ref"
                          name="requestor_ref"
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              requestor_ref: e.target.value,
                            })
                          }
                          defaultValue={selectedOrder.requestor_ref || ""}
                        />
                        <label className="did-floating-label">Votre référence</label>
                      </div>
                    </div>
                  </div>
                  {/* Additional buyer and seller fields */}
                  <div className="row align-items-start">
                    <div className="col">
                      <h5 className="mt-3 mb-3">Acquéreur</h5>
                      <div className="did-floating-label-content">
                        <input
                          className="did-floating-input"
                          id="formAcquereur"
                          name="formAcquereur"
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              buyer_name: e.target.value,
                            })
                          }
                          defaultValue={selectedOrder.buyer_name || ""}
                        />
                        <label className="did-floating-label">Nom, Prénom ou société</label>
                      </div>
                      <div className="did-floating-label-content">
                        <input
                          className="did-floating-input"
                          type="text"
                          id="buyer_address"
                          name="buyer_address"
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              buyer_address: e.target.value,
                            })
                          }
                          defaultValue={selectedOrder.buyer_address || ""}
                        />
                        <label className="did-floating-label">Adresse</label>
                      </div>
                      <div className="did-floating-label-content">
                        <input
                          className="did-floating-input"
                          id="buyer_email"
                          name="buyer_email"
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              buyer_email: e.target.value,
                            })
                          }
                          defaultValue={selectedOrder.buyer_email || ""}
                        />
                        <label className="did-floating-label">Email</label>
                      </div>
                    </div>
                  </div>
                  <div className="row align-items-start">
                    <div className="col">
                      <h5 className="mt-3 mb-3">Vendeur</h5>
                      <div className="did-floating-label-content">
                        <input
                          className="did-floating-input"
                          id="formAcquereur"
                          name="formAcquereur"
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              seller_name: e.target.value,
                            })
                          }
                          defaultValue={selectedOrder.seller_name || ""}
                        />
                        <label className="did-floating-label">Nom, Prénom ou société</label>
                      </div>
                      <div className="did-floating-label-content">
                        <input
                          className="did-floating-input"
                          type="text"
                          id="seller_address"
                          name="seller_address"
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              seller_address: e.target.value,
                            })
                          }
                          defaultValue={selectedOrder.seller_address || ""}
                        />
                        <label className="did-floating-label">Adresse</label>
                      </div>
                      <div className="did-floating-label-content">
                        <input
                          className="did-floating-input"
                          id="seller_email"
                          name="seller_email"
                          onChange={(e) =>
                            setSelectedOrder({
                              ...selectedOrder,
                              seller_email: e.target.value,
                            })
                          }
                          defaultValue={selectedOrder.seller_email || ""}
                        />
                        <label className="did-floating-label">Email</label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-end mt-5">
                  <Button
                    className="w-100 text-light fa-2x"
                    variant="primary"
                    type="submit"
                    style={{ height: "38px", fontWeight: "bold" }}
                  >
                    VALIDER
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}

        {/* Render annexes content when drawerMode is "annexes" */}
        {selectedOrder && drawerMode === "annexes" && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h1 style={{ textAlign: "center", width: "100%" }}>
                ANNEXES
              </h1>

              <div className="ue-drawer-close" onClick={handleCloseDrawer}>
                <svg
                  width="23"
                  height="22"
                  viewBox="0 0 23 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.2812 6.6L11.3433 9.449L8.40537 6.6L6.80597 8.151L9.74388 11L6.80597 13.849L8.40537 15.4L11.3433 12.551L14.2812 15.4L15.8806 13.849L12.9427 11L15.8806 8.151L14.2812 6.6ZM11.3433 0C5.07045 0 0 4.917 0 11C0 17.083 5.07045 22 11.3433 22C17.6161 22 22.6866 17.083 22.6866 11C22.6866 4.917 17.6161 0 11.3433 0ZM11.3433 19.8C6.3409 19.8 2.26866 15.851 2.26866 11C2.26866 6.149 6.3409 2.2 11.3433 2.2C16.3457 2.2 20.4179 6.149 20.4179 11C20.4179 15.851 16.3457 19.8 11.3433 19.8Z"
                    fill="#9A9A9A"
                  />
                </svg>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h5 style={{ textAlign: "center", width: "100%" }}>
                Téléchargez les documents réglementaires associés à ce dossier
              </h5>
            </div>
            <div style={{ marginTop: "1rem" }}>
              {annexes && Object.keys(annexes).length ? (
                Object.keys(annexes).map((category) => (
                  <div key={category} style={{ marginBottom: "1.5rem" }}>
                    <h4 className="px-3 py-2 mb-3">{category}</h4>
                    {Object.keys(annexes[category]).map((group) => (
                      <div key={group} style={{ marginLeft: "1rem", marginBottom: "1rem" }}>
                        {annexes[category][group].map((item, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 mb-3"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              backgroundColor: "#f1f4f4",
                              borderRadius: "6px",

                            }}
                          >
                            <div>{item.libelle_1} - {item.libelle_2}</div>
                            <div className="ml-2">
                              <a href={item.urlfic} target="_blank" rel="noopener noreferrer" style={{ color: "var(--theme-primary)" }}>
                                <span>
                                  <Icon path={mdiArrowDownCircle} size={0.9}
                                    color={"#58D2C3"}
                                    style={{ fill: "#58D2C3", color: "#58D2C3!important" }}
                                  />
                                </span>

                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}

                  </div>
                ))
              ) : (
                <p>Aucune annexe trouvée.</p>
              )}
            </div>
          </>
        )}

      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  entity: state.MeReducer.entity,
  userId: state.MeReducer.id,
});

export default connect(mapStateToProps)(DocumentOrdersTable);
